import React, { useMemo, useState } from 'react';
import { Button } from './Button';
import { Card, CardBody } from './Card';
import { Checkbox } from './Checkbox';
import { EmptyState } from './EmptyState';
import { Input } from './Input';
import { Spinner } from './Spinner';
import { cn } from './utils';
import './DataGrid.css';

export interface DataGridColumn<T> {
  id?: string;
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string;
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
  renderCard?: (row: T, value: React.ReactNode) => React.ReactNode;
}

export interface DataGridProps<T extends { id?: number | string; uuid?: string }> {
  columns: DataGridColumn<T>[];
  data: T[];
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  actions?: (row: T) => React.ReactNode;
  cardLayout?: boolean;
  renderCard?: (row: T) => React.ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  selectable?: boolean;
  selectedIds?: Array<string | number>;
  onSelectionChange?: (ids: Array<string | number>) => void;
  exportFilename?: string;
  className?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

type SortDirection = 'asc' | 'desc';

function getRowId<T extends { id?: number | string; uuid?: string }>(row: T): string | number | undefined {
  return row.id ?? row.uuid;
}

function getColumnId<T>(column: DataGridColumn<T>, index: number): string {
  return column.id ?? column.header ?? String(index);
}

function getCellValue<T>(row: T, column: DataGridColumn<T>): React.ReactNode {
  try {
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }
    const value = row[column.accessor];
    return value !== null && value !== undefined ? String(value) : '-';
  } catch {
    return '-';
  }
}

function getSortValue<T>(row: T, column: DataGridColumn<T>): string | number {
  if (column.sortValue) {
    return column.sortValue(row);
  }
  if (typeof column.accessor === 'function') {
    const rendered = column.accessor(row);
    if (typeof rendered === 'string' || typeof rendered === 'number') {
      return rendered;
    }
    return String(rendered ?? '');
  }
  const value = row[column.accessor];
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }
  return String(value ?? '');
}

function getSearchableText<T>(row: T, column: DataGridColumn<T>): string {
  const sortValue = getSortValue(row, column);
  return String(sortValue).toLowerCase();
}

function escapeCsvValue(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function exportRowsToCsv<T>(
  rows: T[],
  columns: DataGridColumn<T>[],
  filename: string
): void {
  const headers = columns.map((column) => escapeCsvValue(column.header));
  const lines = rows.map((row) =>
    columns
      .map((column) => {
        const raw = getSortValue(row, column);
        return escapeCsvValue(String(raw ?? ''));
      })
      .join(',')
  );
  const csv = [headers.join(','), ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function DataGrid<T extends { id?: number | string; uuid?: string }>({
  columns,
  data,
  isLoading = false,
  onRowClick,
  actions,
  cardLayout = false,
  renderCard,
  searchable = false,
  searchPlaceholder = 'Search…',
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  exportFilename,
  className,
  emptyTitle = 'No data available',
  emptyDescription = 'Try adjusting your filters or check back later',
}: DataGridProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumnId, setSortColumnId] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const visibleRows = useMemo(() => {
    let rows = [...data];

    if (searchable && searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      rows = rows.filter((row) =>
        columns.some((column) => getSearchableText(row, column).includes(query))
      );
    }

    if (sortColumnId) {
      const columnIndex = columns.findIndex((column, index) => getColumnId(column, index) === sortColumnId);
      const column = columnIndex >= 0 ? columns[columnIndex] : undefined;
      if (column?.sortable) {
        rows.sort((a, b) => {
          const aValue = getSortValue(a, column);
          const bValue = getSortValue(b, column);
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
          }
          const comparison = String(aValue).localeCompare(String(bValue), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
          return sortDirection === 'asc' ? comparison : -comparison;
        });
      }
    }

    return rows;
  }, [columns, data, searchable, searchQuery, sortColumnId, sortDirection]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const visibleSelectableIds = useMemo(
    () =>
      visibleRows
        .map((row) => getRowId(row))
        .filter((id): id is string | number => id !== undefined),
    [visibleRows]
  );

  const allVisibleSelected =
    visibleSelectableIds.length > 0 &&
    visibleSelectableIds.every((id) => selectedSet.has(id));

  const someVisibleSelected =
    visibleSelectableIds.some((id) => selectedSet.has(id)) && !allVisibleSelected;

  const handleSort = (column: DataGridColumn<T>, index: number) => {
    if (!column.sortable) {
      return;
    }
    const columnId = getColumnId(column, index);
    if (sortColumnId !== columnId) {
      setSortColumnId(columnId);
      setSortDirection('asc');
      return;
    }
    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }
    setSortColumnId(null);
    setSortDirection('asc');
  };

  const toggleRowSelection = (row: T) => {
    const rowId = getRowId(row);
    if (!onSelectionChange || rowId === undefined) {
      return;
    }
    const next = new Set(selectedSet);
    if (next.has(rowId)) {
      next.delete(rowId);
    } else {
      next.add(rowId);
    }
    onSelectionChange(Array.from(next));
  };

  const toggleAllVisible = () => {
    if (!onSelectionChange) {
      return;
    }
    const next = new Set(selectedSet);
    if (allVisibleSelected) {
      visibleSelectableIds.forEach((id) => next.delete(id));
    } else {
      visibleSelectableIds.forEach((id) => next.add(id));
    }
    onSelectionChange(Array.from(next));
  };

  const showToolbar = searchable || exportFilename || (selectable && selectedIds.length > 0);

  if (isLoading) {
    return (
      <div className={cn('bh-datagrid', className)}>
        <div className="bh-datagrid__loading" role="status">
          <Spinner size="lg" label="Loading data" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn('bh-datagrid', className)}>
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          icon={<span aria-hidden>📭</span>}
        />
      </div>
    );
  }

  const emptyAfterFilter = visibleRows.length === 0;

  const toolbar = showToolbar ? (
    <div className="bh-datagrid__toolbar">
      <div className="bh-datagrid__toolbar-start">
        {searchable ? (
          <Input
            type="search"
            className="bh-datagrid__search"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            aria-label="Search table"
          />
        ) : null}
        {selectable && selectedIds.length > 0 ? (
          <span className="bh-datagrid__selection-meta">{selectedIds.length} selected</span>
        ) : null}
      </div>
      {exportFilename ? (
        <div className="bh-datagrid__toolbar-end">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={visibleRows.length === 0}
            onClick={() => exportRowsToCsv(visibleRows, columns, exportFilename)}
          >
            Export CSV
          </Button>
        </div>
      ) : null}
    </div>
  ) : null;

  if (emptyAfterFilter) {
    return (
      <div className={cn('bh-datagrid', className)}>
        {toolbar}
        <EmptyState
          title="No matching results"
          description="Try a different search term or clear filters."
          icon={<span aria-hidden>🔍</span>}
        />
      </div>
    );
  }

  if (cardLayout && renderCard) {
    return (
      <div className={cn('bh-datagrid', className)}>
        {toolbar}
        <div className="bh-datagrid__cards">
          {visibleRows.map((row, index) => {
            const rowId = getRowId(row);
            const key = rowId ?? index;
            return (
              <div key={key} className="bh-datagrid__card">
                {renderCard(row)}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (cardLayout) {
    return (
      <div className={cn('bh-datagrid', className)}>
        {toolbar}
        <div className="bh-datagrid__cards">
          {visibleRows.map((row, rowIndex) => {
            const rowId = getRowId(row);
            const key = rowId ?? rowIndex;
            const isSelected = rowId !== undefined && selectedSet.has(rowId);

            return (
              <div
                key={key}
                className={cn(
                  'bh-datagrid__card',
                  onRowClick && 'bh-datagrid__card--clickable'
                )}
                onClick={() => onRowClick?.(row)}
              >
                <Card className={cn(isSelected && 'bh-datagrid__row--selected')}>
                  <CardBody>
                    {selectable ? (
                      <div
                        className="mb-3"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={rowId === undefined}
                          onChange={() => toggleRowSelection(row)}
                          aria-label={isSelected ? 'Deselect row' : 'Select row'}
                        />
                      </div>
                    ) : null}
                    {columns.map((column, colIndex) => {
                      const value = getCellValue(row, column);
                      return (
                        <div key={getColumnId(column, colIndex)} className="bh-datagrid__card-field">
                          {column.renderCard ? (
                            column.renderCard(row, value)
                          ) : (
                            <>
                              <div className="bh-datagrid__card-label">{column.header}</div>
                              <div className="bh-datagrid__card-value">{value}</div>
                            </>
                          )}
                        </div>
                      );
                    })}
                    {actions ? (
                      <div
                        className="bh-datagrid__card-actions"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {actions(row)}
                      </div>
                    ) : null}
                  </CardBody>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bh-datagrid', className)}>
      {toolbar}
      <div className="bh-datagrid__scroll">
        <table className="bh-datagrid__table">
          <thead className="bh-datagrid__thead">
            <tr>
              {selectable ? (
                <th className="bh-datagrid__th bh-datagrid__th--select" scope="col">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    disabled={visibleSelectableIds.length === 0}
                    onChange={toggleAllVisible}
                    aria-label={allVisibleSelected ? 'Deselect all rows' : 'Select all rows'}
                  />
                </th>
              ) : null}
              {columns.map((column, index) => {
                const columnId = getColumnId(column, index);
                const isSorted = sortColumnId === columnId;
                return (
                  <th
                    key={columnId}
                    scope="col"
                    className={cn(
                      'bh-datagrid__th',
                      column.className,
                      column.sortable && 'bh-datagrid__th--sortable',
                      isSorted && 'bh-datagrid__th--sorted'
                    )}
                    aria-sort={
                      column.sortable && isSorted
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                    onClick={() => handleSort(column, index)}
                  >
                    <span className="bh-datagrid__th-inner">
                      {column.header}
                      {column.sortable ? (
                        <span className="bh-datagrid__sort-icon" aria-hidden>
                          {isSorted ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      ) : null}
                    </span>
                  </th>
                );
              })}
              {actions ? (
                <th className="bh-datagrid__th bh-datagrid__th--actions" scope="col">
                  Actions
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, rowIndex) => {
              const rowId = getRowId(row);
              const key = rowId ?? rowIndex;
              const isSelected = rowId !== undefined && selectedSet.has(rowId);

              return (
                <tr
                  key={key}
                  className={cn(
                    'bh-datagrid__row',
                    onRowClick && 'bh-datagrid__row--clickable',
                    isSelected && 'bh-datagrid__row--selected'
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {selectable ? (
                    <td
                      className="bh-datagrid__td bh-datagrid__td--select"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={rowId === undefined}
                        onChange={() => toggleRowSelection(row)}
                        aria-label={isSelected ? 'Deselect row' : 'Select row'}
                      />
                    </td>
                  ) : null}
                  {columns.map((column, colIndex) => (
                    <td
                      key={getColumnId(column, colIndex)}
                      className={cn('bh-datagrid__td', column.className)}
                    >
                      {getCellValue(row, column)}
                    </td>
                  ))}
                  {actions ? (
                    <td className="bh-datagrid__td bh-datagrid__td--actions">
                      <div
                        className="bh-datagrid__actions"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {actions(row)}
                      </div>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataGrid;
